import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { SignProduct, SignOption, SignPricing, getSignProducts, getSignOptions, getSignPricing, calculatePrice } from '../../api/signShopCatalog';
import { VISTAPRINT_PRODUCTS } from '../../api/seedSignProducts';

// Use local data until DB tables are created
const getLocalProducts = () => VISTAPRINT_PRODUCTS.map((p, i) => ({
  ...p.product, id: i + 1, image_url: '', thumbnail_url: '', active: true,
} as unknown as SignProduct));

const getLocalOptions = (productId: number | string) => {
  const p = VISTAPRINT_PRODUCTS[Number(productId) - 1];
  return p ? p.options.map((o, i) => ({ ...o, id: i + 1, product_id: productId, active: true, incompatible_with: '' } as unknown as SignOption)) : [];
};

const getLocalPricing = (productId: number | string) => {
  const p = VISTAPRINT_PRODUCTS[Number(productId) - 1];
  return p ? p.pricing.map((t, i) => ({ ...t, id: i + 1, product_id: productId } as unknown as SignPricing)) : [];
};

const fmtPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const CATEGORY_ICONS: Record<string, string> = {
  signs: '🪧', decals: '🏷️', stickers: '🏷️', banners: '🎪', accessories: '🔧',
};

interface ConfiguratorProps {
  onAddToCart?: (item: { product: SignProduct; options: SignOption[]; qty: number; price: number }) => void;
}

export const ProductConfigurator: React.FC<ConfiguratorProps> = ({ onAddToCart }) => {
  const [products, setProducts] = useState<SignProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<SignProduct | null>(null);
  const [options, setOptions] = useState<SignOption[]>([]);
  const [pricing, setPricing] = useState<SignPricing[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, SignOption>>({});
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Load products — try DB first, fall back to local seed data
  useEffect(() => {
    (async () => {
      try {
        const dbProducts = await getSignProducts();
        if (dbProducts.length > 0) {
          setProducts(dbProducts);
        } else {
          setProducts(getLocalProducts());
        }
      } catch {
        setProducts(getLocalProducts());
      }
      setLoading(false);
    })();
  }, []);

  // Load options when product selected
  useEffect(() => {
    if (!selectedProduct) return;
    (async () => {
      try {
        const [dbOptions, dbPricing] = await Promise.all([
          getSignOptions(selectedProduct.id),
          getSignPricing(selectedProduct.id),
        ]);
        if (dbOptions.length > 0) {
          setOptions(dbOptions);
          setPricing(dbPricing);
        } else {
          setOptions(getLocalOptions(selectedProduct.id));
          setPricing(getLocalPricing(selectedProduct.id));
        }
      } catch {
        setOptions(getLocalOptions(selectedProduct.id));
        setPricing(getLocalPricing(selectedProduct.id));
      }

      // Auto-select defaults
      const defaults: Record<string, SignOption> = {};
      const localOpts = getLocalOptions(selectedProduct.id);
      localOpts.forEach(o => { if (o.is_default) defaults[o.option_type] = o; });
      setSelectedOptions(defaults);
      setQuantity(1);
    })();
  }, [selectedProduct]);

  // Group options by type
  const optionGroups = useMemo(() => {
    const groups: Record<string, SignOption[]> = {};
    options.forEach(o => {
      if (!groups[o.option_type]) groups[o.option_type] = [];
      groups[o.option_type].push(o);
    });
    // Sort each group by sort_order
    Object.values(groups).forEach(g => g.sort((a, b) => a.sort_order - b.sort_order));
    return groups;
  }, [options]);

  // Calculate price
  const priceInfo = useMemo(() => {
    if (!selectedProduct) return { perUnit: 0, total: 0, savings: 0 };
    return calculatePrice(
      selectedProduct.base_price_cents,
      Object.values(selectedOptions),
      pricing,
      quantity
    );
  }, [selectedProduct, selectedOptions, pricing, quantity]);

  // Filter products by category
  const filteredProducts = categoryFilter === 'all' ? products : products.filter(p => p.category === categoryFilter);
  const categories = Array.from(new Set(products.map(p => p.category)));

  // Option type labels
  const optionTypeLabels: Record<string, string> = {
    shape: 'Shape', size: 'Size', material: 'Material', printing: 'Print Quality',
    mounting: 'Mounting', corners: 'Corners', surface: 'Application', frame: 'Frame Color',
    sides: 'Printed Sides', edges: 'Edges',
  };

  // Parse size for canvas preview
  const selectedSize = selectedOptions.size;
  const canvasPreview = useMemo(() => {
    if (!selectedSize) return { width: 300, height: 200, label: '' };
    const match = selectedSize.option_label.match(/(\d+\.?\d*)"?\s*[×x]\s*(\d+\.?\d*)/);
    if (match) {
      const w = parseFloat(match[1]);
      const h = parseFloat(match[2]);
      const scale = Math.min(300 / w, 200 / h, 20);
      return { width: Math.round(w * scale), height: Math.round(h * scale), label: `${w}" × ${h}"` };
    }
    // Feet for banners
    const ftMatch = selectedSize.option_label.match(/(\d+\.?\d*)'\s*[×x]\s*(\d+\.?\d*)'/);
    if (ftMatch) {
      const w = parseFloat(ftMatch[1]) * 12;
      const h = parseFloat(ftMatch[2]) * 12;
      const scale = Math.min(300 / w, 200 / h, 5);
      return { width: Math.round(w * scale), height: Math.round(h * scale), label: selectedSize.option_label };
    }
    return { width: 300, height: 200, label: selectedSize.option_label };
  }, [selectedSize]);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;

  // ── Product Selection View ──
  if (!selectedProduct) {
    return (
      <div>
        {/* Category filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button onClick={() => setCategoryFilter('all')}
            className={`px-4 py-2 text-sm rounded-lg whitespace-nowrap ${categoryFilter === 'all' ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
            All Products
          </button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setCategoryFilter(cat)}
              className={`px-4 py-2 text-sm rounded-lg whitespace-nowrap capitalize ${categoryFilter === cat ? 'bg-brand-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {CATEGORY_ICONS[cat] || ''} {cat}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map(p => (
            <div key={p.id} onClick={() => setSelectedProduct(p)}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-40 bg-gradient-to-br from-gray-50 to-gray-100 flex flex-col items-center justify-center">
                <span className="text-4xl mb-2">{CATEGORY_ICONS[p.category] || '📦'}</span>
                <span className="text-xs text-gray-400 capitalize">{p.category}</span>
              </div>
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">{p.name}</h3>
                <p className="text-xs text-gray-500 mb-3 line-clamp-2">{p.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">From {fmtPrice(p.base_price_cents)}</span>
                  <Badge color="blue">{p.supplier}</Badge>
                </div>
                {p.production_days && (
                  <p className="text-xs text-gray-400 mt-1">{p.production_days}-day production</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Configurator View ──
  return (
    <div>
      {/* Back button */}
      <button onClick={() => { setSelectedProduct(null); setSelectedOptions({}); }}
        className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
        Back to Products
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Preview */}
        <div className="lg:col-span-1">
          <Card>
            <div className="text-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">{selectedProduct.name}</h2>
              <p className="text-sm text-gray-500 mt-1">{selectedProduct.description}</p>
            </div>

            {/* Size preview */}
            <div className="bg-gray-50 rounded-lg p-4 flex items-center justify-center mb-4" style={{ minHeight: 220 }}>
              <div className="relative" style={{ width: canvasPreview.width, height: canvasPreview.height }}>
                <div className="absolute inset-0 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-white">
                  <div className="text-center">
                    <span className="text-3xl">{CATEGORY_ICONS[selectedProduct.category] || '📦'}</span>
                    <p className="text-xs text-gray-400 mt-1">{canvasPreview.label}</p>
                  </div>
                </div>
                {/* Bleed zone indicator */}
                <div className="absolute -inset-1 border border-red-200 rounded opacity-50" />
              </div>
            </div>

            {/* Specs */}
            <div className="text-xs text-gray-500 space-y-1">
              <div className="flex justify-between"><span>Supplier</span><Badge color="blue">{selectedProduct.supplier}</Badge></div>
              <div className="flex justify-between"><span>Production</span><span>{selectedProduct.production_days} days</span></div>
              <div className="flex justify-between"><span>Min Qty</span><span>{selectedProduct.min_qty}</span></div>
              <div className="flex justify-between"><span>Max Qty</span><span>{selectedProduct.max_qty}</span></div>
            </div>
          </Card>
        </div>

        {/* Right: Options + Pricing */}
        <div className="lg:col-span-2 space-y-4">
          {/* Option Groups */}
          {Object.entries(optionGroups).map(([type, opts]) => (
            <Card key={type}>
              <h3 className="font-semibold text-gray-900 mb-3 capitalize">{optionTypeLabels[type] || type}</h3>
              <div className="flex flex-wrap gap-2">
                {opts.map(opt => {
                  const isSelected = selectedOptions[type]?.option_value === opt.option_value;
                  return (
                    <button key={opt.option_value}
                      onClick={() => setSelectedOptions(prev => ({ ...prev, [type]: opt }))}
                      className={`px-4 py-2.5 text-sm rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-brand-600 bg-brand-50 text-brand-700 font-medium'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}>
                      <span>{opt.option_label}</span>
                      {opt.price_delta_cents !== 0 && (
                        <span className={`ml-1.5 text-xs ${opt.price_delta_cents > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                          {opt.price_delta_cents > 0 ? '+' : ''}{fmtPrice(opt.price_delta_cents)}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}

          {/* Quantity */}
          <Card>
            <h3 className="font-semibold text-gray-900 mb-3">Quantity</h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {[1, 2, 5, 10, 25, 50, 100].filter(q => q <= (selectedProduct.max_qty || 250)).map(q => (
                <button key={q} onClick={() => setQuantity(q)}
                  className={`px-4 py-2 text-sm rounded-lg border-2 transition-all ${
                    quantity === q ? 'border-brand-600 bg-brand-50 text-brand-700 font-medium' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}>
                  {q}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">Custom:</span>
              <input type="number" min={selectedProduct.min_qty} max={selectedProduct.max_qty}
                value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-20 px-3 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>
          </Card>

          {/* Pricing Summary */}
          <Card>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Price Summary</h3>
              {priceInfo.savings > 0 && (
                <Badge color="green">{priceInfo.savings}% volume discount</Badge>
              )}
            </div>

            {/* Selected options recap */}
            <div className="space-y-1 mb-4 text-sm">
              {Object.entries(selectedOptions).map(([type, opt]) => (
                <div key={type} className="flex justify-between text-gray-600">
                  <span className="capitalize">{optionTypeLabels[type] || type}</span>
                  <span className="font-medium text-gray-900">{opt.option_label}
                    {opt.price_delta_cents !== 0 && (
                      <span className={`ml-1 text-xs ${opt.price_delta_cents > 0 ? 'text-orange-500' : 'text-green-500'}`}>
                        ({opt.price_delta_cents > 0 ? '+' : ''}{fmtPrice(opt.price_delta_cents)})
                      </span>
                    )}
                  </span>
                </div>
              ))}
              <div className="flex justify-between text-gray-600">
                <span>Quantity</span>
                <span className="font-medium text-gray-900">{quantity}</span>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Per unit</span>
                <span className="font-semibold text-gray-900">{fmtPrice(priceInfo.perUnit)}</span>
              </div>
              <div className="flex justify-between text-lg">
                <span className="font-semibold text-gray-900">Total</span>
                <span className="font-bold text-brand-600">{fmtPrice(priceInfo.total)}</span>
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <Button className="flex-1" onClick={() => {
                if (onAddToCart) {
                  onAddToCart({
                    product: selectedProduct,
                    options: Object.values(selectedOptions),
                    qty: quantity,
                    price: priceInfo.total,
                  });
                }
              }}>
                Add to Cart — {fmtPrice(priceInfo.total)}
              </Button>
              <Button variant="secondary" onClick={() => {
                window.open(selectedProduct.supplier_url, '_blank');
              }}>
                Design on Vistaprint
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};
